import React, { useEffect, useRef, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Capacitor } from '@capacitor/core';
import { Contacts as NativeContacts } from '@capacitor/contacts';
import api from '../api';
import { parseContactCsv } from '../utils/contactCsv';

export default function Contacts() {
  const [contacts, setContacts]       = useState([]);
  const [tags, setTags]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(1);
  const [search, setSearch]           = useState('');
  const [filterTag, setFilterTag]     = useState('');
  const [filterOptIn, setFilterOptIn]       = useState('');
  const [filterFlowData, setFilterFlowData] = useState('');
  const [viewingFlow, setViewingFlow]       = useState(null); // { contact, response }
  const [selected, setSelected]             = useState([]);
  const [loading, setLoading]         = useState(false);

  // Contact modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState({ name: '', phone: '', email: '', tags: [], notes: '', opt_in_status: true });

  // CSV import modal
  const [showCsvModal, setShowCsvModal] = useState(false);

  // Phone import modal
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  // Tag panel
  const [showTagPanel, setShowTagPanel] = useState(false);
  const [tagForm, setTagForm]           = useState({ name: '', color: '#6366f1' });
  const [editingTag, setEditingTag]     = useState(null);
  const [savingTag, setSavingTag]       = useState(false);

  const LIMIT = 50;

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (search)     params.search  = search;
      if (filterTag)  params.tag     = filterTag;
      if (filterOptIn) params.opt_in = filterOptIn;
      if (filterFlowData) params.has_flow_data = filterFlowData;
      const r = await api.get('/contacts', { params });
      setContacts(r.data.contacts);
      setTotal(r.data.total);
    } catch { toast.error('Failed to load contacts'); }
    setLoading(false);
  }, [page, search, filterTag, filterOptIn, filterFlowData]);

  const fetchTags = useCallback(() =>
    api.get('/tags').then(r => setTags(r.data)), []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);
  useEffect(() => { fetchTags(); }, [fetchTags]);

  // ── Contact CRUD ────────────────────────────────────────────────────────────
  async function openCreate() {
    setEditing(null);
    // Pre-fetch (or create) the current datetime default tag and pre-select it
    let defaultTagId = null;
    try {
      const r = await api.get('/contacts/default-tag');
      defaultTagId = r.data._id;
      // Make sure it appears in the tag list
      setTags(ts => ts.find(t => t._id === defaultTagId) ? ts : [r.data, ...ts]);
    } catch { /* non-fatal — user can still pick a tag manually */ }
    setForm({ name: '', phone: '', email: '', tags: defaultTagId ? [defaultTagId] : [], notes: '', opt_in_status: true });
    setShowModal(true);
  }
  function openEdit(c) {
    setEditing(c);
    setForm({ name: c.name || '', phone: c.phone || '', email: c.email || '', tags: c.tags?.map(t => t._id) || [], notes: c.notes || '', opt_in_status: c.opt_in_status !== false });
    setShowModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (form.tags.length === 0) {
      toast.error('Please select at least one tag before saving.');
      return;
    }
    try {
      if (editing) {
        const r = await api.put(`/contacts/${editing._id}`, form);
        setContacts(cs => cs.map(c => c._id === editing._id ? r.data : c));
        toast.success('Contact updated');
      } else {
        const r = await api.post('/contacts', form);
        setContacts(cs => [r.data, ...cs]);
        toast.success('Contact created');
      }
      setShowModal(false);
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this contact?')) return;
    await api.delete(`/contacts/${id}`);
    setContacts(cs => cs.filter(c => c._id !== id));
    toast.success('Deleted');
  }

  async function bulkTag(tagId, action = 'add') {
    if (!selected.length) return;
    await api.post('/contacts/bulk/tag', { contact_ids: selected, tag_id: tagId, action });
    toast.success(`${action === 'add' ? 'Tagged' : 'Untagged'} ${selected.length} contacts`);
    setSelected([]);
    fetchContacts();
  }

  function toggleSelect(id) {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  }

  // ── Tag CRUD ─────────────────────────────────────────────────────────────────
  function openCreateTag() {
    setEditingTag(null);
    setTagForm({ name: '', color: '#6366f1' });
  }
  function openEditTag(t) {
    setEditingTag(t);
    setTagForm({ name: t.name, color: t.color });
  }

  async function handleSaveTag(e) {
    e.preventDefault();
    setSavingTag(true);
    try {
      if (editingTag) {
        const r = await api.put(`/tags/${editingTag._id}`, tagForm);
        setTags(ts => ts.map(t => t._id === editingTag._id ? { ...r.data, contact_count: t.contact_count } : t));
        toast.success('Tag updated');
      } else {
        const r = await api.post('/tags', tagForm);
        setTags(ts => [...ts, { ...r.data, contact_count: 0 }]);
        toast.success('Tag created');
      }
      setEditingTag(null);
      setTagForm({ name: '', color: '#6366f1' });
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    setSavingTag(false);
  }

  async function handleDeleteTag(id) {
    if (!window.confirm('Delete this tag and remove it from all contacts?')) return;
    await api.delete(`/tags/${id}`);
    setTags(ts => ts.filter(t => t._id !== id));
    if (filterTag === id) setFilterTag('');
    fetchContacts();
    toast.success('Tag deleted');
  }

  const pages = Math.ceil(total / LIMIT);

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

      {/* ── Main contacts area ─────────────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="page-header">
          <h1 className="page-title">
            Contacts <span style={{ fontSize: 15, color: 'var(--text-muted)', fontWeight: 400 }}>({total})</span>
          </h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={() => setShowTagPanel(p => !p)}>
              🏷️ {showTagPanel ? 'Hide Tags' : 'Manage Tags'}
              {tags.length > 0 && <span className="badge badge-purple" style={{ marginLeft: 6 }}>{tags.length}</span>}
            </button>
            <button className="btn btn-ghost" onClick={() => setShowPhoneModal(true)}>📱 Import from Phone</button>
            <button className="btn btn-ghost" onClick={() => setShowCsvModal(true)}>📥 Import CSV</button>
            <button className="btn btn-primary" onClick={openCreate}>+ Add Contact</button>
          </div>
        </div>

        {/* Search / filter bar */}
        <div className="search-bar">
          <input className="input search-input" placeholder="Search name, phone, email…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />

          <select className="select" style={{ width: 160 }} value={filterTag}
            onChange={e => { setFilterTag(e.target.value); setPage(1); }}>
            <option value="">All Tags</option>
            {tags.map(t => (
              <option key={t._id} value={t._id}>{t.name} ({t.contact_count})</option>
            ))}
          </select>

          <select className="select" style={{ width: 130 }} value={filterOptIn}
            onChange={e => { setFilterOptIn(e.target.value); setPage(1); }}>
            <option value="">All Status</option>
            <option value="true">Opted In</option>
            <option value="false">Opted Out</option>
          </select>

          <select className="select" style={{ width: 150 }} value={filterFlowData}
            onChange={e => { setFilterFlowData(e.target.value); setPage(1); }}>
            <option value="">All Contacts</option>
            <option value="true">Has Flow Data</option>
          </select>

          {selected.length > 0 && (
            <select className="select" style={{ width: 170 }} defaultValue=""
              onChange={e => { if (e.target.value) bulkTag(e.target.value, 'add'); }}>
              <option value="">+ Tag {selected.length} selected…</option>
              {tags.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
            </select>
          )}
        </div>

        {/* Tag filter chips */}
        {tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            <button
              className="tag-chip"
              style={{ color: filterTag === '' ? 'var(--primary-light)' : 'var(--text-muted)', borderColor: filterTag === '' ? 'var(--primary)' : 'var(--border)', cursor: 'pointer', background: 'none', fontSize: 12, padding: '3px 10px' }}
              onClick={() => setFilterTag('')}>
              All
            </button>
            {tags.map(t => (
              <button key={t._id}
                className="tag-chip"
                style={{ color: filterTag === t._id ? '#fff' : t.color, borderColor: t.color, background: filterTag === t._id ? t.color : 'transparent', cursor: 'pointer', fontSize: 12, padding: '3px 10px' }}
                onClick={() => setFilterTag(filterTag === t._id ? '' : t._id)}>
                {t.name}
                <span style={{ marginLeft: 4, opacity: 0.7 }}>{t.contact_count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Contacts table */}
        <div className="card">
          {loading
            ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>
            : contacts.length === 0
              ? <div className="empty-state">
                  <div className="empty-state-icon">👥</div>
                  <div className="empty-state-text">No contacts found</div>
                </div>
              : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>
                          <input type="checkbox"
                            checked={selected.length === contacts.length && contacts.length > 0}
                            onChange={e => setSelected(e.target.checked ? contacts.map(c => c._id) : [])} />
                        </th>
                        <th>Name</th><th>Phone</th><th>Email</th>
                        <th>Tags</th><th>Status</th><th>Source</th><th>Flow Data</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map(c => (
                        <tr key={c._id}>
                          <td><input type="checkbox" checked={selected.includes(c._id)} onChange={() => toggleSelect(c._id)} /></td>
                          <td style={{ fontWeight: 500 }}>{c.name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                          <td style={{ fontSize: 13 }}>{c.phone || '—'}</td>
                          <td style={{ fontSize: 13 }}>{c.email || '—'}</td>
                          <td>
                            {c.tags?.map(t => (
                              <span key={t._id} className="tag-chip"
                                style={{ color: t.color, borderColor: t.color, marginRight: 4, fontSize: 11 }}>
                                {t.name}
                              </span>
                            ))}
                          </td>
                          <td>
                            <span className={c.opt_in_status ? 'badge badge-green' : 'badge badge-red'}>
                              {c.opt_in_status ? 'Opted In' : 'Opted Out'}
                            </span>
                          </td>
                          <td><span className="badge badge-gray">{c.source}</span></td>
                          <td>
                            {c.flow_responses?.length > 0 ? (
                              <button
                                onClick={() => setViewingFlow({ contact: c, response: c.flow_responses[c.flow_responses.length - 1] })}
                                style={{ background: 'rgba(99,102,241,0.12)', color: 'var(--primary-light)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 99, padding: '2px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                              >
                                📋 {c.flow_responses.length} {c.flow_responses.length === 1 ? 'response' : 'responses'}
                              </button>
                            ) : (
                              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                            )}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)} style={{ marginRight: 4 }}>Edit</button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c._id)}>Del</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
          }
          {pages > 1 && (
            <div className="pagination">
              <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>←</button>
              <span>Page {page} of {pages}</span>
              <button className="btn btn-ghost btn-sm" disabled={page === pages} onClick={() => setPage(p => p + 1)}>→</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Tag panel (right sidebar) ──────────────────────────────────────── */}
      {showTagPanel && (
        <div style={{ width: 280, flexShrink: 0 }}>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div className="card-title" style={{ margin: 0 }}>🏷️ Tags</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowTagPanel(false)}>✕</button>
            </div>

            {/* Create / Edit form */}
            <form onSubmit={handleSaveTag} style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label className="label">{editingTag ? 'Edit tag' : 'New tag'}</label>
                <input className="input" required placeholder="Tag name…" value={tagForm.name}
                  onChange={e => setTagForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="color" value={tagForm.color}
                  onChange={e => setTagForm(f => ({ ...f, color: e.target.value }))}
                  style={{ width: 36, height: 36, border: 'none', background: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 }} />
                <span className="tag-chip" style={{ color: tagForm.color, borderColor: tagForm.color, fontSize: 12 }}>
                  {tagForm.name || 'Preview'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={savingTag} style={{ flex: 1, justifyContent: 'center' }}>
                  {savingTag ? '…' : editingTag ? '✓ Update' : '+ Create'}
                </button>
                {editingTag && (
                  <button type="button" className="btn btn-ghost btn-sm"
                    onClick={() => { setEditingTag(null); setTagForm({ name: '', color: '#6366f1' }); }}>
                    Cancel
                  </button>
                )}
              </div>
            </form>

            {/* Tag list */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              {tags.length === 0
                ? <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>No tags yet</p>
                : tags.map(t => (
                  <div key={t._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(51,65,85,0.4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                      <span className="tag-chip" style={{ color: t.color, borderColor: t.color, fontSize: 11, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.name}
                      </span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{t.contact_count}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button className="btn btn-ghost btn-sm" style={{ padding: '3px 8px', fontSize: 11 }}
                        onClick={() => openEditTag(t)}>Edit</button>
                      <button className="btn btn-danger btn-sm" style={{ padding: '3px 8px', fontSize: 11 }}
                        onClick={() => handleDeleteTag(t._id)}>Del</button>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {/* ── Flow Data viewer ──────────────────────────────────────────────── */}
      {viewingFlow && (
        <FlowDataModal
          contact={viewingFlow.contact}
          onClose={() => setViewingFlow(null)}
        />
      )}

      {/* ── Phone Import modal ────────────────────────────────────────────── */}
      {showPhoneModal && (
        <PhoneImportModal
          onClose={() => setShowPhoneModal(false)}
          onImported={({ inserted, updated, skipped }) => {
            setShowPhoneModal(false);
            fetchContacts();
            fetchTags();
            toast.success(`Imported: ${inserted} new, ${updated} updated${skipped ? `, ${skipped} skipped` : ''}`);
          }}
        />
      )}

      {/* ── CSV Import modal ──────────────────────────────────────────────── */}
      {showCsvModal && (
        <CsvImportModal
          onClose={() => setShowCsvModal(false)}
          onImported={({ inserted, updated, skipped }) => {
            fetchContacts();
            fetchTags();
            if (inserted + updated) toast.success(`Imported: ${inserted} new, ${updated} updated${skipped ? `, ${skipped} skipped` : ''}`);
            else toast.error('No contacts imported. Review the errors below.');
          }}
        />
      )}

      {/* ── Contact modal ─────────────────────────────────────────────────── */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">{editing ? 'Edit Contact' : 'New Contact'}</div>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="label">Name</label>
                <input className="input" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" />
              </div>
              <div className="form-group">
                <label className="label">Phone (with country code)</label>
                <input className="input" value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+263771234567" />
              </div>
              <div className="form-group">
                <label className="label">Email</label>
                <input className="input" type="email" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="name@example.com" />
              </div>

              {/* Tags — required */}
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label className="label" style={{ margin: 0 }}>
                    Tags <span style={{ color: 'var(--danger)' }}>*</span>
                    {form.tags.length === 0 && (
                      <span style={{ color: 'var(--danger)', fontSize: 11, marginLeft: 8, fontWeight: 400 }}>
                        — required
                      </span>
                    )}
                  </label>
                  <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                    onClick={() => { setShowModal(false); setShowTagPanel(true); }}>
                    + New tag
                  </button>
                </div>

                {tags.length === 0 ? (
                  <div style={{ padding: '10px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid var(--danger)', borderRadius: 8, fontSize: 13, color: 'var(--danger)' }}>
                    No tags yet. Click <strong>"+ New tag"</strong> above to create one first.
                  </div>
                ) : (
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: 8,
                    padding: 10, borderRadius: 8,
                    border: `1px solid ${form.tags.length === 0 ? 'var(--danger)' : 'var(--border)'}`,
                    background: 'var(--surface2)',
                  }}>
                    {tags.map(t => {
                      const checked = form.tags.includes(t._id);
                      return (
                        <label key={t._id} style={{ cursor: 'pointer' }}>
                          <input type="checkbox" checked={checked} style={{ display: 'none' }}
                            onChange={e => setForm(f => ({
                              ...f,
                              tags: e.target.checked
                                ? [...f.tags, t._id]
                                : f.tags.filter(x => x !== t._id),
                            }))} />
                          <span className="tag-chip" style={{
                            color: checked ? '#fff' : t.color,
                            borderColor: t.color,
                            background: checked ? t.color : 'transparent',
                            fontSize: 12, padding: '4px 10px',
                            userSelect: 'none',
                          }}>
                            {checked ? '✓ ' : ''}{t.name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="label">Notes</label>
                <textarea className="textarea" value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.opt_in_status !== false}
                    onChange={e => setForm(f => ({ ...f, opt_in_status: e.target.checked }))} />
                  <span className="label" style={{ margin: 0 }}>Opted In to messages</span>
                </label>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save Contact</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Flow Data Modal ────────────────────────────────────────────────────────────
function FlowDataModal({ contact, onClose }) {
  const responses = contact.flow_responses || [];

  function formatData(dataMap) {
    if (!dataMap) return [];
    const obj = dataMap instanceof Map ? Object.fromEntries(dataMap) : dataMap;
    return Object.entries(obj).filter(([k]) => !['flow_token', 'version'].includes(k));
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 580 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div className="modal-title" style={{ marginBottom: 2 }}>📋 Flow Responses</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              {contact.name || contact.phone} — {responses.length} {responses.length === 1 ? 'response' : 'responses'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {responses.map((resp, i) => {
            const entries = formatData(resp.data);
            return (
              <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{resp.flow_name || 'Flow'}</span>
                    {resp.trigger_keyword && (
                      <code style={{ marginLeft: 8, background: 'rgba(99,102,241,0.12)', color: 'var(--primary-light)', padding: '1px 6px', borderRadius: 4, fontSize: 11 }}>
                        {resp.trigger_keyword}
                      </code>
                    )}
                  </div>
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                    {resp.completed_at ? new Date(resp.completed_at).toLocaleString() : '—'}
                  </span>
                </div>
                {entries.length === 0 ? (
                  <div style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: 13 }}>No form fields recorded.</div>
                ) : (
                  <div style={{ padding: '10px 14px' }}>
                    {entries.map(([key, val]) => (
                      <div key={key} style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500, minWidth: 140, textTransform: 'capitalize', flexShrink: 0 }}>
                          {key.replace(/_/g, ' ')}
                        </div>
                        <div style={{ fontSize: 13, wordBreak: 'break-all' }}>
                          {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── vCard parser ──────────────────────────────────────────────────────────────
function parseVcf(text) {
  const contacts = [];
  const cards = text.split(/BEGIN:VCARD/i).slice(1);
  for (const card of cards) {
    const get = (field) => {
      const re = new RegExp(`^${field}[^:]*:(.+)$`, 'im');
      const m = card.match(re);
      return m ? m[1].trim() : '';
    };
    const name  = get('FN') || get('N').replace(/;/g, ' ').trim();
    const phone = get('TEL').replace(/[^\d+]/g, '').replace(/^\+/, '');
    const email = get('EMAIL').toLowerCase();
    if (phone || email) contacts.push({ name, phone, email });
  }
  return contacts;
}

// ── Phone Import Modal ─────────────────────────────────────────────────────────
function defaultPhoneTagName() {
  return new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).replace(',', '') + ' Phone Import';
}

function PhoneImportModal({ onClose, onImported }) {
  const fileRef = useRef();
  const isNative = Capacitor.isNativePlatform();
  const hasWebContactPicker = typeof navigator !== 'undefined' && 'contacts' in navigator && 'ContactsManager' in window;
  const hasContactPicker = isNative || hasWebContactPicker;

  const [rows, setRows]         = useState([]);
  const [tagName, setTagName]   = useState(defaultPhoneTagName);
  const [fileName, setFileName] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  async function pickFromPhone() {
    setError('');
    try {
      let parsed;
      if (isNative) {
        // The official plugin opens the OS contact picker. Android requests
        // READ_CONTACTS when needed; iOS grants access to the chosen contact.
        const contact = await NativeContacts.pickContact();
        parsed = [{
          name: contact.displayName || contact.name?.formatted || [contact.name?.givenName, contact.name?.familyName].filter(Boolean).join(' '),
          phone: (contact.phoneNumbers?.find(p => p.pref)?.value || contact.phoneNumbers?.[0]?.value || '').replace(/[^\d+]/g, '').replace(/^\+/, ''),
          email: (contact.emails?.find(e => e.pref)?.value || contact.emails?.[0]?.value || '').toLowerCase(),
        }].filter(r => r.phone || r.email);
      } else {
        const results = await navigator.contacts.select(['name', 'tel', 'email'], { multiple: true });
        parsed = results.map(c => ({
          name:  Array.isArray(c.name)  ? c.name[0]  : (c.name  || ''),
          phone: Array.isArray(c.tel)   ? (c.tel[0]  || '').replace(/[^\d+]/g, '').replace(/^\+/, '') : '',
          email: Array.isArray(c.email) ? (c.email[0] || '').toLowerCase() : '',
        })).filter(r => r.phone || r.email);
      }

      if (parsed.length === 0) { setError('No contacts selected or none had a phone/email.'); return; }
      setRows(current => {
        const combined = isNative ? [...current, ...parsed] : parsed;
        const unique = combined.filter((row, index, all) => {
          const key = row.phone || row.email;
          return all.findIndex(other => (other.phone || other.email) === key) === index;
        });
        setFileName(`${unique.length} contact${unique.length !== 1 ? 's' : ''} selected from phone`);
        return unique;
      });
    } catch (e) {
      const cancelled = e.name === 'AbortError' || e.code === 'OS-PLUG-CONT-0006';
      if (!cancelled) {
        setError(e.code === 'OS-PLUG-CONT-0020'
          ? 'Contacts permission was denied. Enable Contacts access in your phone settings and try again.'
          : 'Could not access phone contacts: ' + (e.message || 'Unknown error'));
      }
    }
  }

  function handleVcfFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseVcf(ev.target.result);
      if (parsed.length === 0) {
        setError('No valid contacts found in the .vcf file.');
        return;
      }
      setRows(parsed);
      setFileName(file.name);
    };
    reader.readAsText(file);
  }

  async function handleImport() {
    if (!rows.length) { setError('Please select contacts first.'); return; }
    if (!tagName.trim()) { setError('Tag name is required.'); return; }
    setLoading(true);
    try {
      const r = await api.post('/contacts/import-csv', { rows, tagName: tagName.trim(), source: 'phone' });
      onImported(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Import failed');
    }
    setLoading(false);
  }

  const thS = { padding: '7px 10px', textAlign: 'left', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' };
  const tdS = { padding: '7px 10px', fontSize: 13 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="card" style={{ width: '100%', maxWidth: 580, maxHeight: '90vh', overflowY: 'auto', padding: 28 }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>📱 Import from Phone</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Step 1: pick contacts */}
        <div style={{ marginBottom: 20 }}>
          <div className="label" style={{ marginBottom: 10 }}>Step 1 — Choose contacts</div>

          {hasContactPicker && (
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', marginBottom: 10 }}
              onClick={pickFromPhone}
              disabled={loading}
            >
              📲 {isNative && rows.length ? 'Pick another contact' : 'Pick contacts from this device'}
            </button>
          )}

          {isNative && (
            <div style={{ margin: '-2px 0 12px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
              Your phone opens its secure contact picker. Pick again to add more contacts.
            </div>
          )}

          <div
            onClick={() => fileRef.current.click()}
            style={{
              border: '2px dashed var(--border)', borderRadius: 10,
              padding: '20px 16px', textAlign: 'center', cursor: 'pointer',
              color: 'var(--text-muted)',
            }}
          >
            {fileName
              ? <><strong style={{ color: 'var(--text)' }}>{fileName}</strong><br /><span style={{ fontSize: 12 }}>{rows.length} valid contacts — click to change</span></>
              : <>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>📂</div>
                  <div>Upload a <strong>.vcf</strong> file exported from your phone</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    On Android: Contacts → Export → Save as .vcf<br />
                    On iPhone: Contacts → Share Contact → export .vcf
                  </div>
                </>
            }
            <input ref={fileRef} type="file" accept=".vcf,text/vcard" style={{ display: 'none' }} onChange={handleVcfFile} />
          </div>

          {!hasContactPicker && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
              💡 Direct contact picking is not available in this browser. Use the .vcf upload instead.
            </div>
          )}
        </div>

        {/* Preview */}
        {rows.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
              Showing {Math.min(rows.length, 5)} of {rows.length} contacts
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: 'var(--surface2)' }}>
                  <tr><th style={thS}>Name</th><th style={thS}>Phone</th><th style={thS}>Email</th></tr>
                </thead>
                <tbody>
                  {rows.slice(0, 5).map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={tdS}>{r.name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={tdS}>{r.phone || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={tdS}>{r.email || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Step 2: tag */}
        <div style={{ marginBottom: 16 }}>
          <div className="label" style={{ marginBottom: 6 }}>Step 2 — Tag applied to imported contacts</div>
          <input className="input" value={tagName} onChange={e => setTagName(e.target.value)} placeholder="e.g. Phone Import June" />
        </div>

        {error && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleImport} disabled={loading || !rows.length}>
            {loading ? 'Importing…' : `Import ${rows.length} contact${rows.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CSV Import Modal ───────────────────────────────────────────────────────────

function defaultImportTagName() {
  return new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).replace(',', '') + ' Import';
}

function CsvImportErrors({ errors, title }) {
  if (!errors?.length) return null;
  return (
    <div style={{ marginBottom: 16, padding: 12, border: '1px solid var(--danger)', borderRadius: 8, fontSize: 12 }}>
      <strong>{title} ({errors.length})</strong>
      <div style={{ maxHeight: 130, overflowY: 'auto', marginTop: 6 }}>
        {errors.map((item, index) => (
          <div key={`${item.rowNumber}-${index}`}>
            Row {item.rowNumber}{item.phone ? ` · ${item.phone}` : ''}: {item.reason}
          </div>
        ))}
      </div>
    </div>
  );
}

function CsvImportModal({ onClose, onImported }) {
  const fileRef = useRef();
  const selectionRef = useRef(0);
  const [rows, setRows]         = useState([]);
  const [preview, setPreview]   = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult]     = useState(null);
  const [tagName, setTagName]   = useState(defaultImportTagName);
  const [fileName, setFileName] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const selection = ++selectionRef.current;
    setFileName(file.name);
    setError('');
    setRows([]);
    setPreview(null);
    setResult(null);
    const reader = new FileReader();
    reader.onload = async ev => {
      if (selection !== selectionRef.current) return;
      const parsed = parseContactCsv(ev.target.result);
      if (!parsed.some(row => row.phone || row.email)) {
        setError('No contact rows found. CSV must have a header row with at least a "phone" or "email" column.');
        return;
      }
      setRows(parsed);
      setPreviewing(true);
      try {
        const response = await api.post('/contacts/import-csv/preview', { rows: parsed });
        if (selection === selectionRef.current) setPreview(response.data);
      } catch (err) {
        if (selection === selectionRef.current) setError(err.response?.data?.error || 'Could not preview this CSV');
      } finally {
        if (selection === selectionRef.current) setPreviewing(false);
      }
    };
    reader.onerror = () => {
      if (selection === selectionRef.current) setError('Could not read this CSV file');
    };
    reader.readAsText(file);
  }

  async function handleImport() {
    if (!preview) { setError('Please preview a CSV file first.'); return; }
    if (!tagName.trim()) { setError('Tag name is required.'); return; }
    setLoading(true);
    try {
      const r = await api.post('/contacts/import-csv', { rows, tagName: tagName.trim() });
      setResult(r.data);
      onImported(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Import failed');
    }
    setLoading(false);
  }

  const thS = { padding: '7px 10px', textAlign: 'left', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' };
  const tdS = { padding: '7px 10px', fontSize: 13 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="card" style={{ width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto', padding: 28 }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Import Contacts from CSV</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8, fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span>
            Recognised columns: <code>name</code>, <code>country_code</code>, <code>phone</code>, <code>email</code>.
            Local numbers without a country code are treated as Zimbabwean. Valid numbers are corrected to international digits.
            Rows with invalid phone numbers are skipped and listed below. Other valid rows continue importing.
          </span>
          <button
            className="btn btn-ghost btn-sm"
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            onClick={() => {
              const csv = 'name,country_code,phone,email\nJohn Doe,,0771234567,john@example.com\nJane Smith,263,772345678,jane@example.com\nBob Jones,,+263 77 345 6789,bob@example.com\nEmail Only,,,emailonly@example.com';
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'contacts_sample.csv';
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            ⬇ Sample CSV
          </button>
        </div>

        {/* Drop zone */}
        <div
          onClick={() => fileRef.current.click()}
          style={{ border: '2px dashed var(--border)', borderRadius: 10, padding: '24px 16px', textAlign: 'center', cursor: 'pointer', marginBottom: 16, color: 'var(--text-muted)' }}
        >
          {fileName
            ? <><strong style={{ color: 'var(--text)' }}>{fileName}</strong><br /><span style={{ fontSize: 12 }}>{previewing ? 'Checking numbers…' : `${rows.length} rows — click to change`}</span></>
            : <><div style={{ fontSize: 28, marginBottom: 6 }}>📄</div>Click to choose a CSV file</>
          }
          <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleFile} />
        </div>

        {/* Preview table */}
        {preview && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
              Showing {Math.min(preview.rows.length, 5)} of {preview.rows.length} rows
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: 'var(--surface2)' }}>
                  <tr><th style={thS}>Row</th><th style={thS}>Name</th><th style={thS}>Original phone</th><th style={thS}>Imported phone</th><th style={thS}>Email</th></tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 5).map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={tdS}>{r.rowNumber}</td>
                      <td style={tdS}>{r.name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={tdS}>{r.originalPhone || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={tdS}>{r.phoneError ? <span style={{ color: 'var(--danger)' }}>Skipped</span> : (r.phone || <span style={{ color: 'var(--text-muted)' }}>—</span>)}</td>
                      <td style={tdS}>{r.email || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <CsvImportErrors errors={result ? null : preview?.errors} title="Invalid numbers (rows will be skipped)" />

        {/* Tag name */}
        <div style={{ marginBottom: 16 }}>
          <label className="label">Tag applied to imported contacts</label>
          <input className="input" value={tagName} onChange={e => setTagName(e.target.value)} placeholder="e.g. June Campaign List" />
        </div>

        {error && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        {result && (
          <div style={{ marginBottom: 12, fontSize: 13 }}>
            Imported: {result.inserted} new, {result.updated} updated, {result.skipped} skipped.
          </div>
        )}
        <CsvImportErrors errors={result?.errors} title="Import errors" />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={loading}>{result ? 'Done' : 'Cancel'}</button>
          {!result && <button className="btn btn-primary" onClick={handleImport} disabled={loading || !preview || previewing}>
            {loading ? 'Importing…' : 'Import CSV'}
          </button>}
        </div>
      </div>
    </div>
  );
}
